import { createContext, useContext, useEffect, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { subscribeFleetDriver } from "../services/fleetDriverService";
import { db } from "../firebase";
import { AuthContext } from "./AuthContext";
import { formatLastSeenDateTime, getDriverPresence } from "../utils/driverPresence";
import { sendFleetMessageToDriver } from "../services/fleetConversationService";

const ChatContext = createContext();
const INITIAL_CHAT_LIMIT = 30;
const CHAT_LOAD_MORE_STEP = 30;

const toMillis = (rawDate) => {
  if (!rawDate) return 0;
  if (typeof rawDate.toMillis === "function") return rawDate.toMillis();
  if (typeof rawDate._seconds === "number") return rawDate._seconds * 1000;
  return new Date(rawDate).getTime() || 0;
};

const getVisibleMessages = (allMessages, visibleLimit) => {
  const sliceStart = Math.max(0, allMessages.length - visibleLimit);
  return {
    visibleMessages: allMessages.slice(sliceStart),
    hasMore: sliceStart > 0,
  };
};

export const ChatProvider = ({ children }) => {
  const { getFleetID } = useContext(AuthContext);
  const [openChats, setOpenChats] = useState([]);
  const [driversStatus, setDriversStatus] = useState({});
  const [chatMessageLimits, setChatMessageLimits] = useState({});
  const chatsRef = useRef([]);
  const chatMessageLimitsRef = useRef({});
  chatsRef.current = openChats;
  chatMessageLimitsRef.current = chatMessageLimits;

  const openChat = async ({ id, name, type, currentOrder, driverStatus, profilePic }) => {
    const key = `${id}-${type}-${currentOrder || "none"}`;

    setOpenChats((prev) => {
      const existingChat = prev.find((chat) => chat.key === key);
      if (existingChat) {
        return prev.map((chat) => (chat.key === key ? { ...chat, isMinimized: false } : chat));
      }
      return [
        ...prev,
        {
          key,
          id,
          name,
          type,
          currentOrder,
          driverStatus,
          profilePic,
          messages: [],
          allMessages: [],
          hasMore: false,
          isMinimized: false,
        },
      ];
    });
    setChatMessageLimits((prev) => ({ ...prev, [key]: INITIAL_CHAT_LIMIT }));
  };

  const sendMessage = async (chatKey, text, type, currentOrder) => {
    const fleetId = getFleetID();
    if (!fleetId) return;

    const activeChat = chatsRef.current.find((chat) => chat.key === chatKey);
    const userId = activeChat?.id || chatKey.split("-")[0];
    const orderId = currentOrder || activeChat?.currentOrder || null;
    const messageDate = new Date();
    const tempMessageId = `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const messageTemp = {
      message: text,
      createdAt: messageDate.toISOString(),
      role: "FLEET",
      read: false,
      pending: true,
      tempMessageId,
    };

    setOpenChats((prev) =>
      prev.map((chat) =>
        chat.key === chatKey
          ? {
              ...chat,
              messages: [...chat.messages, messageTemp],
              allMessages: [...(chat.allMessages || []), messageTemp],
            }
          : chat
      )
    );

    try {
      await sendFleetMessageToDriver({
        fleetId,
        driverId: userId,
        message: text,
        orderId,
      });

      setOpenChats((prev) =>
        prev.map((chat) =>
          chat.key === chatKey
            ? {
                ...chat,
                messages: chat.messages.filter((m) => m.tempMessageId !== tempMessageId),
                allMessages: (chat.allMessages || []).filter((m) => m.tempMessageId !== tempMessageId),
              }
            : chat
        )
      );
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
      setOpenChats((prev) =>
        prev.map((chat) =>
          chat.key === chatKey
            ? {
                ...chat,
                messages: chat.messages.map((m) =>
                  m.tempMessageId === tempMessageId ? { ...m, pending: false, error: true } : m
                ),
                allMessages: (chat.allMessages || []).map((m) =>
                  m.tempMessageId === tempMessageId ? { ...m, pending: false, error: true } : m
                ),
              }
            : chat
        )
      );
      throw err;
    }
  };

  useEffect(() => {
    if (openChats.length === 0) return;
    const fleetId = getFleetID();
    if (!fleetId) return;

    const unsubscribers = openChats.map((chat) => {
      const conversationsRef = collection(db, "fleets", fleetId, "conversations");
      if (chat.type !== "All" && !chat.currentOrder) {
        return () => {};
      }
      const chatQuery =
        chat.type === "All"
          ? query(conversationsRef, where("userId", "==", chat.id))
          : query(conversationsRef, where("orderId", "==", chat.currentOrder));

      return onSnapshot(
        chatQuery,
        (snap) => {
          const sortedMessages = snap.docs
            .map((conversationDoc) => ({ docId: conversationDoc.id, ...conversationDoc.data() }))
            .sort(
              (a, b) => toMillis(a.createdAt) - toMillis(b.createdAt)
            );
          const visibleLimit = chatMessageLimitsRef.current[chat.key] || INITIAL_CHAT_LIMIT;
          const { visibleMessages, hasMore } = getVisibleMessages(sortedMessages, visibleLimit);
          setOpenChats((prev) =>
            prev.map((existingChat) =>
              existingChat.key === chat.key
                ? {
                    ...existingChat,
                    messages: visibleMessages,
                    allMessages: sortedMessages,
                    hasMore,
                  }
                : existingChat
            )
          );
        },
        (error) => {
          console.error("Erro ao assinar mensagens:", error);
        }
      );
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [openChats.map((chat) => chat.key).join("|"), getFleetID]);

  useEffect(() => {
    if (openChats.length === 0) return;

    const uniqueDriverIds = Array.from(new Set(openChats.map((chat) => chat.id)));
    const unsubscribers = uniqueDriverIds.map((driverId) => {
      return subscribeFleetDriver(
        getFleetID(), driverId,
        (data) => {
          const isOnlineSignal = typeof data.isOnline === "boolean" ? data.isOnline : null;
          const { isOnline } = getDriverPresence(isOnlineSignal, data.lastSeen);
          setDriversStatus((prev) => ({
            ...prev,
            [driverId]: {
              isOnlineSignal,
              lastSeenRaw: data.lastSeen || null,
              isOnline,
              lastSeen: formatLastSeenDateTime(data.lastSeen),
            },
          }));
        },
        (error) => {
          console.error("Erro ao assinar status dos drivers:", error);
          setDriversStatus(prev => ({ ...prev, [driverId]: {} }));
        }
      );
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [openChats.map((chat) => chat.id).join("|")]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDriversStatus((prev) => {
        let changed = false;
        const next = Object.entries(prev).reduce((acc, [driverId, statusData]) => {
          const { isOnlineSignal = null, lastSeenRaw = null } = statusData || {};
          const { isOnline } = getDriverPresence(isOnlineSignal, lastSeenRaw);
          const lastSeen = formatLastSeenDateTime(lastSeenRaw);
          if (statusData?.isOnline !== isOnline || statusData?.lastSeen !== lastSeen) {
            changed = true;
            acc[driverId] = { ...statusData, isOnline, lastSeen };
            return acc;
          }
          acc[driverId] = statusData;
          return acc;
        }, {});
        return changed ? next : prev;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const closeChat = (chatKey) => {
    setOpenChats((prev) => prev.filter((c) => c.key !== chatKey));
    setChatMessageLimits((prev) => {
      const next = { ...prev };
      delete next[chatKey];
      chatMessageLimitsRef.current = next;
      return next;
    });
  };

  const toggleMinimize = (chatKey) =>
    setOpenChats((prev) =>
      prev.map((c) => (c.key === chatKey ? { ...c, isMinimized: !c.isMinimized } : c))
    );

  const loadMoreMessages = (chatKey) => {
    const chatToLoad = chatsRef.current.find((chat) => chat.key === chatKey);
    if (!chatToLoad?.hasMore) return;

    const nextLimit =
      (chatMessageLimitsRef.current[chatKey] || INITIAL_CHAT_LIMIT) + CHAT_LOAD_MORE_STEP;
    const nextLimits = {
      ...chatMessageLimitsRef.current,
      [chatKey]: nextLimit,
    };
    chatMessageLimitsRef.current = nextLimits;
    setChatMessageLimits(nextLimits);

    setOpenChats((prev) =>
      prev.map((chat) => {
        if (chat.key !== chatKey) return chat;
        const allMessages = chat.allMessages || chat.messages || [];
        const { visibleMessages, hasMore } = getVisibleMessages(allMessages, nextLimit);
        return {
          ...chat,
          messages: visibleMessages,
          hasMore,
        };
      })
    );
  };

  return (
    <ChatContext.Provider
      value={{
        openChats,
        openChat,
        sendMessage,
        closeChat,
        toggleMinimize,
        driversStatus,
        loadMoreMessages,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);
